'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import {
  buildResultsPerPage,
  loadPaginationActions,
} from '@coveo/atomic-react';
import {
  DEFAULT_SEARCH_PAGE_SIZE,
  PAGE_SIZE_OPTIONS,
} from '@lib/constants/coveo';
import { useFlags } from '@lib/flags/amplitude/client';
import { Flags } from '@lib/flags/flagConstants';
import { useController } from '@lib/hooks/useController';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { useMemo, useState } from 'react';
import { Dropdown, DropdownOption } from 'ui-components';
import { useEngine } from '../EngineContext';
import {
  getResultsPerPageFromLocalStorage,
  setResultsPerPageToLocalStorage,
} from '../utils/resultsPerPageStorage';
import { ResultsPerPageProps } from './ResultsPerPage.types';

export const ResultsPerPage = ({ className }: ResultsPerPageProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
  }>();
  const { flags } = useFlags();
  const flag = flags[Flags.INCREASE_DEFAULT_RESULTS_PER_PAGE];
  const defaultPageSize = flag?.payload?.pageSize || DEFAULT_SEARCH_PAGE_SIZE;

  const resultsLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.results-per-page')
      ?.value ?? 'Results';

  const resultsPerPageAriaLabel =
    searchLabelsMicrocopy?.find(
      (m) => m.key === 'search.results-per-page-aria-label'
    )?.value ?? '';

  // Compute the initial value once on mount to prevent controller re-creation.
  // Re-creating the controller dispatches registerNumberOfResults which sets
  // defaultNumberOfResults = numberOfResults, causing Coveo to omit
  // numberOfResults from the URL hash (it drops values equal to the default).
  // URL hash takes priority so that legacy redirect URLs (e.g. rows=96 → #numberOfResults=96)
  // survive the useEngineState ready→loading→ready remount cycle.
  const [initialNumberOfResults] = useState(() => {
    const hash = window.location.hash.slice(1);
    const fromUrl = parseInt(
      new URLSearchParams(hash).get('numberOfResults') ?? '',
      10
    );
    if (PAGE_SIZE_OPTIONS.includes(fromUrl)) return fromUrl;
    return getResultsPerPageFromLocalStorage() ?? defaultPageSize;
  });

  const eng = useEngine();
  const controller = useMemo(
    () =>
      buildResultsPerPage(eng, {
        initialState: { numberOfResults: initialNumberOfResults },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eng]
  );
  const state = useController(controller);

  const options: DropdownOption[] = PAGE_SIZE_OPTIONS.map((size) => ({
    label: String(size),
    value: String(size),
  }));

  const baseOption =
    options.find((opt) => opt.value === String(state.numberOfResults)) ??
    options[0];

  const selectedOption: DropdownOption = useMemo(
    () => ({
      ...baseOption,
      label: (
        <>
          <span className="text-grey-600">{resultsLabel}:</span>{' '}
          <span className="font-normal text-[#141414] opacity-90">
            {baseOption.label}
          </span>
        </>
      ),
    }),
    [baseOption, resultsLabel]
  );

  const handleSelect = (option: DropdownOption) => {
    const numberOfResults = parseInt(option.value, 10);
    controller.set(numberOfResults);
    // Update defaultNumberOfResults so SPA hash-change navigation doesn't revert the
    // setting via restoreSearchParameters. registerNumberOfResults is a pure Redux
    // state action — it does NOT dispatch executeSearch, so no second search fires.
    // Trade-off: once the default equals the user's choice, Coveo omits numberOfResults
    // from the URL hash, so the preference is persisted via localStorage only and won't
    // survive copy-pasted or shared URLs. Page-size is treated as a personal preference
    // rather than a shareable filter parameter.
    eng.dispatch(
      loadPaginationActions(eng).registerNumberOfResults(numberOfResults)
    );
    setResultsPerPageToLocalStorage(numberOfResults);
  };

  return (
    <div className={className}>
      <Dropdown
        id="results-per-page"
        options={options}
        selectedOption={selectedOption}
        onSelect={handleSelect}
        isDarkTheme={false}
        buttonClassName="bg-transparent"
        ariaLabel={
          resultsPerPageAriaLabel || `Show ${baseOption.label} results per page`
        }
        className="[&_[role=option]]:m-0.5 [&_[role=option]]:p-1.5"
      />
    </div>
  );
};
